type Modifier = {
  priceImpact: number
  quantity?: number
}

type ComboProduct = {
  priceImpact: number
  quantity?: number
  modifiers?: Modifier[]
}

type DiscountType = 'percentage' | 'cash' | 'currency' | '2x1'

type Product = {
  price: number
  modifiers?: Modifier[]
  comboProducts?: ComboProduct[]
  discountType?: DiscountType
  discountAmount?: number
  flatFeeSurcharge?: number
}

type ProductPricing = {
  fullPrice: number
  finalPrice: number
}

function calculateModifiersImpact(modifiers?: Modifier[]): number {
  if (!modifiers) return 0
  return modifiers
    .filter(modifier => (modifier.quantity ?? 1) > 0)
    .map(modifier => modifier.priceImpact * (modifier.quantity || 1))
    .reduce((total, impact) => total + impact, 0)
}

/**
 * This function calculates the pricing of a product based on its base price,
 * modifiers, combo products, discounts and surcharges.
 * It returns an object containing the full price (before discounts) and the final price (after discounts).
 * @param product - The product object.
 * @returns An object with fullPrice and finalPrice properties.
 */
function calculateProductPricing(product: Product): ProductPricing {
  let fullPrice = product.price + (product.flatFeeSurcharge || 0)
  if (product.modifiers && product.modifiers.length > 0) {
    const modifiersImpact = calculateModifiersImpact(product.modifiers)
    fullPrice = product.price + modifiersImpact
  } else if (product.comboProducts && product.comboProducts.length > 0) {
    const productsImpact = product.comboProducts
      .filter(comboProduct => (comboProduct.quantity ?? 1) > 0)
      .map(
        comboProduct =>
          (comboProduct.priceImpact + calculateModifiersImpact(comboProduct.modifiers)) *
          (comboProduct.quantity || 1)
      )
      .reduce((total, impact) => total + impact, 0)

    fullPrice = product.price + productsImpact
  }
  let finalPrice = fullPrice
  if (product.discountType) {
    if (product.discountType === 'percentage') {
      finalPrice = Math.round(fullPrice * (1 - (product.discountAmount || 0) / 100))
    } else if (['cash', 'currency'].includes(product.discountType)) {
      finalPrice = fullPrice - (product.discountAmount || 0)
    }
  }
  return {
    fullPrice,
    finalPrice
  }
}

export default {
  calculateProductPricing
}
